import { useEffect } from 'react';
import { X, Sprout } from 'lucide-react';
import { FarmFeature, getImageStyleFromUrl } from '../types';

interface FeatureDetailModalProps {
  feature: FarmFeature | null;
  onClose: () => void;
}

export default function FeatureDetailModal({ feature, onClose }: FeatureDetailModalProps) {
  useEffect(() => {
    if (!feature) return;

    // Stop page scroll behind modal
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [feature]);

  if (!feature) return null;

  return (
    <div
      id="feature-detail-backdrop"
      className="fixed inset-0 z-50 bg-stone-950/85 backdrop-blur-md flex items-center justify-center p-4 sm:p-6 overflow-y-auto animate-fadeIn"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-[#FDFCF9] text-[#1A241F] w-full max-w-4xl rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl border border-[#E6E1D4] flex flex-col md:flex-row max-h-[90vh]">
        {/* Left Side: Photo */}
        <div className="relative w-full md:w-1/2 bg-[#0E1E16] flex items-center justify-center group h-[280px] sm:h-[360px] md:h-auto">
          <img
            src={feature.imageUrl}
            alt={feature.name}
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover"
            style={getImageStyleFromUrl(feature.imageUrl)}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 pointer-events-none" />
          
          {feature.tag && (
            <span className="estate-chip-amber absolute bottom-4 left-4 shadow-md">
              {feature.tag}
            </span>
          )}
        </div>

        {/* Right Side: Narrative Details */}
        <div className="w-full md:w-1/2 p-6 sm:p-8 flex flex-col justify-between overflow-y-auto max-h-[48vh] md:max-h-none">
          <div>
            {/* Header / Dismiss */}
            <div className="flex justify-between items-center mb-5">
              <span className="estate-chip">
                <Sprout size={12} className="text-emerald-700" />
                Estate Grounds
              </span>
              <button
                onClick={onClose}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-[#65756C] hover:text-[#14532D] hover:bg-[#F0FDF4] rounded-full transition-colors cursor-pointer border border-[#E6E1D4]"
                title="Close"
              >
                <span>Close</span>
                <X size={14} />
              </button>
            </div>

            {/* Title & Description */}
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold leading-tight text-[#1A241F] mb-4">
              {feature.name}
            </h2>
            
            <div className="text-sm sm:text-base text-[#37463E] leading-relaxed space-y-3 font-normal">
              {feature.description.split('\n').map((paragraph, idx) => (
                <p key={idx} className="leading-relaxed">{paragraph}</p>
              ))}
            </div>
          </div>

          <div className="pt-6 border-t border-[#E6E1D4] mt-6 flex items-center justify-between text-xs text-[#65756C]">
            <span>Mithran Farm House, Sirudavoor</span>
            <span className="font-medium text-[#14532D]">Private Grounds</span>
          </div>
        </div>
      </div>
    </div>
  );
}
